import { format, max } from "date-fns";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useMemo } from "react";
import { toast } from "@/components/ui/sonner";
import { useUpdateCart } from "@/features/cart/api/hooks/use-update-cart";
import { useCartStore } from "@/features/cart/components/cart-provider";
import { haveSaturdays, haveSundays } from "@/features/cart/utils/dates";
import { useDiet } from "@/features/diets/hooks/use-diet";
import { useGetDiscountConfig } from "@/features/discounts/api/hooks/use-get-discount-config";
import { useGetDiscountInfo } from "@/features/discounts/api/hooks/use-get-discount-info";
import { DietDateManager } from "@/lib/helpers/diet-dates";
import { formatPrice } from "@/lib/utils";

export type DiscountProgressMessageVariant =
  | "gratz"
  | "nextTier"
  | "nextTierMax";

export type DiscountTierData = {
  discountAmountLabel: string;
  days: number;
  discount: number;
  discountLabel: string;
  tierIndex: number;
};

export type DiscountProgressDisplayTier = {
  fromAmount: number;
  fromAmountLabel: string;
  valueLabel: string;
};

export type DiscountProgressType = "amount" | "percentage";

type DiscountTierState = {
  current: DiscountTierData;
  next: DiscountTierData | null;
};

const EMPTY_DISCOUNT_TIER: DiscountTierState = {
  current: {
    discountAmountLabel: "",
    days: 0,
    discount: 0,
    discountLabel: "",
    tierIndex: 0,
  },
  next: null,
};

const formatAmount = (value: number) => {
  return formatPrice(value / 100).replace(",00", "");
};

const formatDiscountValue = (type: DiscountProgressType, value: number) => {
  if (type === "percentage") {
    return `${value}%`;
  }

  return formatAmount(value);
};

export const formatCtaDiscountValue = ({
  days,
  pricePerDay,
  totalPrice,
  type,
  value,
}: {
  days: number;
  pricePerDay: number;
  totalPrice: number;
  type: DiscountProgressType;
  value: number;
}) => {
  if (type === "amount") {
    return formatAmount(value);
  }

  const futureTotalPrice = totalPrice + days * pricePerDay;

  return formatAmount(Math.round(futureTotalPrice * value));
};

export const calculateDiscountTier = ({
  type,
  pricePerDay,
  tiers,
  totalPrice,
}: {
  type: DiscountProgressType;
  pricePerDay: number;
  tiers: Array<{
    fromAmount: number;
    value: number;
  }>;
  totalPrice: number;
}): DiscountTierState => {
  if (tiers.length === 0) {
    return EMPTY_DISCOUNT_TIER;
  }

  const totalPriceInCents = totalPrice * 100;
  const firstTier = tiers.find((tier) => tier.fromAmount > totalPriceInCents);
  const firstTierIndex = tiers.findIndex(
    (tier) => tier.fromAmount > totalPriceInCents,
  );

  if (firstTierIndex === -1) {
    const currentDiscount = tiers.at(-1)?.value ?? 0;

    return {
      current: {
        discountAmountLabel: formatCtaDiscountValue({
          days: 0,
          pricePerDay,
          totalPrice,
          type,
          value: currentDiscount,
        }),
        days: 0,
        discount: currentDiscount,
        discountLabel: formatDiscountValue(type, currentDiscount),
        tierIndex: -1,
      },
      next: null,
    };
  }

  const currentTier = tiers[firstTierIndex - 1];
  const nextTierDays =
    firstTier && pricePerDay > 0
      ? Math.max(
          Math.ceil(
            (firstTier.fromAmount - totalPriceInCents) / (pricePerDay * 100),
          ),
          0,
        )
      : 0;

  return {
    current: {
      discountAmountLabel: formatCtaDiscountValue({
        days: 0,
        pricePerDay,
        totalPrice,
        type,
        value: currentTier?.value ?? 0,
      }),
      days: 0,
      discount: currentTier?.value ?? 0,
      discountLabel: formatDiscountValue(type, currentTier?.value ?? 0),
      tierIndex: firstTierIndex - 1,
    },
    next: {
      discountAmountLabel: formatCtaDiscountValue({
        days: nextTierDays,
        pricePerDay,
        totalPrice,
        type,
        value: firstTier?.value ?? 0,
      }),
      days: nextTierDays,
      discount: firstTier?.value ?? 0,
      discountLabel: formatDiscountValue(type, firstTier?.value ?? 0),
      tierIndex: firstTierIndex,
    },
  };
};

export const useDiscountCartProgress = ({
  onlyHeader = false,
}: {
  onlyHeader?: boolean;
}) => {
  const locale = useLocale();
  const t = useTranslations();
  const cartId = useCartStore((state) => state.cartId);
  const discountCode = useCartStore((state) => state.discountCode);
  const items = useCartStore((state) => state.items);
  const pricing = useCartStore((state) => state.pricing);
  const updateStoredCart = useCartStore((state) => state.updateCart);
  const currentCartItem = items[0]?.configurationItem;
  const hasMultipleDiets = items.length > 1;
  const { mutateAsync: updateCart, isPending: isUpdatingCart } =
    useUpdateCart();

  const { data: discountInfo } = useGetDiscountInfo({
    options: {
      enabled: !!discountCode && !hasMultipleDiets,
    },
    code: discountCode ?? "",
  });

  const { data: discountConfig } = useGetDiscountConfig({
    options: {
      enabled: !!discountInfo?.id && !hasMultipleDiets,
    },
    id: discountInfo?.id ?? "",
  });

  const { data: diet } = useDiet({
    dietId: currentCartItem?.dietId ?? "",
    options: {
      enabled: !!currentCartItem?.dietId && !hasMultipleDiets,
    },
  });

  const numberOfDays = currentCartItem?.dates.length ?? 0;

  const automaticDiscountsTotal = useMemo(() => {
    return (
      pricing?.discounts.reduce((sum, discount) => {
        if (discount.type !== "automatic") {
          return sum;
        }

        return sum + discount.amount.gross;
      }, 0) ?? 0
    );
  }, [pricing?.discounts]);

  const totalPrice = Math.max(
    (pricing?.before.gross ?? 0) - automaticDiscountsTotal,
    0,
  );
  const totalPriceInCents = totalPrice * 100;
  const pricePerDay = totalPrice / (numberOfDays || 1);

  const amountTiers = discountConfig?.amountTiers ?? [];
  const amountPercentageTiers = discountConfig?.amountPercentageTiers ?? [];
  const hasAmountTiers = amountTiers.length > 0;
  const hasPercentageTiers = amountPercentageTiers.length > 0;
  const hasValidDiscountProgressConfig = hasAmountTiers !== hasPercentageTiers;
  const tierType: DiscountProgressType = hasAmountTiers
    ? "amount"
    : "percentage";

  const progressTierValues = useMemo(() => {
    if (!hasValidDiscountProgressConfig) {
      return [];
    }

    if (hasAmountTiers) {
      return amountTiers.map((tier) => ({
        fromAmount: tier.fromAmount,
        value: tier.amount,
      }));
    }

    return amountPercentageTiers.map((tier) => ({
      fromAmount: tier.fromAmount,
      value: tier.discountPercent,
    }));
  }, [
    amountPercentageTiers,
    amountTiers,
    hasAmountTiers,
    hasValidDiscountProgressConfig,
  ]);

  const discountTier = useMemo(() => {
    return calculateDiscountTier({
      type: tierType,
      totalPrice,
      pricePerDay,
      tiers: progressTierValues,
    });
  }, [pricePerDay, progressTierValues, tierType, totalPrice]);

  const amountTiersLength = progressTierValues.length;
  let messageVariant: DiscountProgressMessageVariant = "nextTier";

  if (discountTier.current.tierIndex === -1) {
    messageVariant = "gratz";
  } else if (discountTier.next?.tierIndex === amountTiersLength - 1) {
    messageVariant = "nextTierMax";
  }

  const shouldShowAlreadyHaveDiscount =
    discountTier.current.tierIndex !== -1 &&
    discountTier.current.tierIndex !== 0;

  const displayOnlyHeader = discountTier.current.tierIndex === -1 || onlyHeader;
  const shouldShowContent = discountTier.current.tierIndex !== -1;
  const isAddDaysDisabled =
    isUpdatingCart ||
    !cartId ||
    !currentCartItem?.id ||
    !diet ||
    !discountTier.next ||
    discountTier.next.days <= 0;

  const progressValue = useMemo(() => {
    const highestTierAmount = progressTierValues.at(-1)?.fromAmount ?? 0;

    if (highestTierAmount <= 0) {
      return 0;
    }

    return Math.min((totalPriceInCents * 100) / highestTierAmount, 100);
  }, [progressTierValues, totalPriceInCents]);

  const progressTiers = useMemo<DiscountProgressDisplayTier[]>(() => {
    if (!hasValidDiscountProgressConfig) {
      return [];
    }

    return progressTierValues.map((tier) => ({
      fromAmount: tier.fromAmount,
      fromAmountLabel: formatAmount(tier.fromAmount),
      valueLabel: formatDiscountValue(tierType, tier.value),
    }));
  }, [hasValidDiscountProgressConfig, progressTierValues, tierType]);

  const handleAddDaysToCart = useCallback(async () => {
    if (!cartId) {
      return;
    }

    if (!currentCartItem?.id) {
      return;
    }

    if (!diet) {
      return;
    }

    if (!discountTier.next) {
      return;
    }

    const currentDates = currentCartItem.dates.map((date) => new Date(date));
    const lastSelectedDate = max(currentDates);

    if (!lastSelectedDate) {
      return;
    }

    const dateManager = new DietDateManager({
      diet,
      locale,
      saturdays: haveSaturdays(currentCartItem.dates),
      sundays: haveSundays(currentCartItem.dates),
    });

    const nextDates = dateManager.findNextAvailableDatesArray(
      lastSelectedDate,
      discountTier.next.days,
    );

    if (nextDates.length === 0) {
      return;
    }

    const updatedItem = {
      ...currentCartItem,
      dates: [
        ...currentCartItem.dates,
        ...nextDates.map((date) => format(date, "yyyy-MM-dd")),
      ],
    };

    try {
      const data = await updateCart({
        cartId,
        item: updatedItem,
      });

      updateStoredCart(data.cartId, data.cart, "update", t);
    } catch (error: unknown) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  }, [
    cartId,
    currentCartItem,
    diet,
    discountTier.next,
    locale,
    t,
    updateCart,
    updateStoredCart,
  ]);

  return {
    currentDiscountAmountLabel: discountTier.current.discountAmountLabel,
    currentDiscountLabel: discountTier.current.discountLabel,
    discountType: tierType,
    discountConfig,
    displayOnlyHeader,
    handleAddDaysToCart,
    hasMultipleDiets,
    isAddDaysDisabled,
    messageVariant,
    nextTier: discountTier.next,
    progressTiers,
    progressValue,
    shouldShowAlreadyHaveDiscount,
    shouldShowContent,
  };
};
